import { IonContent, IonPage, useIonToast } from '@ionic/react';
import { Box, Paper, Stepper, Step, StepLabel } from '@mui/material';
import { 
  Title as TitleIcon, 
  Description as DescriptionIcon,
  Image as ImageIcon,
  Send as SendIcon,
  
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PostFormData } from '../types/post';
import { useAuthContext } from '../contexts/AuthContext';
import { Company, Contractor, Ambassador } from '../types/user';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import '../theme/newPost.css';

// Import components
import {
  PostHeader,
  TitleStep,
  DescriptionStep,
  ImagesStep,
  ReviewStep,
  StepNavigation,
  StepIcon
} from '../components/newPost';

export const NewPost: React.FC = () => {
  const { t } = useTranslation();
  const { userData: currentUser, user: authUser, isLoading } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PostFormData>({
    title: '',
    description: '',
    images: []
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPostId, setEditPostId] = useState<string | null>(null);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const location = useLocation();
  const [presentToast] = useIonToast();
  const history = useHistory();
  const maxImages = 5;
  const maxTitleChars = 50;
  const maxDescriptionChars = 10000;
  const [activeStep, setActiveStep] = useState(0);

  const MAX_IMAGE_SIZE = 1024 * 1024;
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
  const MAX_IMAGE_DIMENSION = 1200;

  const steps = [
    {
      label: t('new_post.step_title'),
      icon: <TitleIcon />,
    },
    {
      label: t('new_post.step_description'),
      icon: <DescriptionIcon />,
    },
    {
      label: t('new_post.step_images'),
      icon: <ImageIcon />,
    },
    {
      label: t('new_post.step_review'),
      icon: <SendIcon />,
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title
    }));
  };

  const handleDescriptionChange = (description: string) => {
    setFormData(prev => ({
      ...prev,
      description
    }));
  };

  const resizeImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
            width = MAX_IMAGE_DIMENSION;
          } else {
            width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
            height = MAX_IMAGE_DIMENSION;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not create blob'));
              return;
            }
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.9
        );
      };

      img.onerror = () => reject(new Error('Error loading image'));
    });
  };

  const validateAndProcessImage = async (file: File): Promise<File | null> => {
    // Check file type
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      presentToast({
        message: t('new_post.invalid_format'),
        duration: 2000,
        color: 'warning'
      });
      return null;
    }

    try {
      // Always resize images to ensure consistent quality and size
      const resizedFile = await resizeImage(file);
      
      // After resizing, check if the file is still too large
      if (resizedFile.size > MAX_IMAGE_SIZE) {
        presentToast({
          message: t('new_post.resize_error'),
          duration: 2000,
          color: 'warning'
        });
        return null;
      }

      return resizedFile;
    } catch (error) {
      console.error('Error processing image:', error);
      presentToast({
        message: t('new_post.resize_error'),
        duration: 2000,
        color: 'warning'
      });
      return null;
    }
  };

  const handleImageSelect = async () => {
    try {
      const totalImages = formData.images.length + existingImageUrls.length;
      if (totalImages >= maxImages) {
        presentToast({
          message: t('new_post.max_images_error', { max: maxImages }),
          duration: 2000,
          color: 'warning'
        });
        return;
      }

      // Check permissions first
      const permissionStatus = await Camera.checkPermissions();
      
      // If permission is not granted, request it
      if (permissionStatus.photos !== 'granted') {
        const requestResult = await Camera.requestPermissions({
          permissions: ['photos']
        });
        
        // If still not granted after request, show error
        if (requestResult.photos !== 'granted') {
          presentToast({
            message: t('profile.gallery_permission_denied'),
            duration: 3000,
            color: 'warning'
          });
          return;
        }
      }

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
        width: MAX_IMAGE_DIMENSION,
        correctOrientation: true
      });

      const response = await fetch(image.webPath!);
      const blob = await response.blob();
      const file = new File([blob], `post-image-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const processedFile = await validateAndProcessImage(file);
      if (processedFile) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, processedFile]
        }));
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'User cancelled photos app') {
        console.error('Error selecting photo:', error);
        presentToast({
          message: t('new_post.image_error'),
          duration: 2000,
          color: 'danger'
        });
      }
    }
  };

  const removeNewImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeExistingImage = (index: number) => {
    setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!isLoading && !authUser) {
      history.replace('/auth');
    }

    // Check for edit mode
    const searchParams = new URLSearchParams(location.search);
    const postId = searchParams.get('edit');
    if (postId) {
      setIsEditMode(true);
      setEditPostId(postId);
      loadExistingPost(postId);
    }
  }, [authUser, isLoading, history, location]);

  const loadExistingPost = async (postId: string) => {
    try {
      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (postDoc.exists()) {
        const postData = postDoc.data();
        setFormData(prev => ({
          ...prev,
          title: postData.title,
          description: postData.description
        }));
        setExistingImageUrls(postData.images || []);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      presentToast({
        message: t('edit_post.load_error'),
        duration: 2000,
        color: 'danger'
      });
      history.goBack();
    }
  };

  const handleSubmit = async () => {
    if (!authUser?.uid || !currentUser) {
      presentToast({
        message: t('new_post.auth_error'),
        duration: 2000,
        color: 'warning'
      });
      return;
    }

    if (formData.title.length > maxTitleChars) {
      presentToast({
        message: t('new_post.max_title_chars_error', { max: maxTitleChars }),
        duration: 2000,
        color: 'warning'
      });
      return;
    }

    if (formData.description.length > maxDescriptionChars) {
      presentToast({
        message: t('new_post.max_description_chars_error', { max: maxDescriptionChars }),
        duration: 2000,
        color: 'warning'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload images first
      const imageUrls = await Promise.all(
        formData.images.map(async (file) => {
          const imageRef = ref(storage, `posts/${authUser.uid}/${Date.now()}-${file.name}`);
          // Set proper metadata for the upload
          const metadata = {
            contentType: 'image/jpeg', // We're always converting to JPEG in resizeImage
            customMetadata: {
              'originalName': file.name
            }
          };
          
          try {
            await uploadBytes(imageRef, file, metadata);
            return await getDownloadURL(imageRef);
          } catch (uploadError) {
            console.error('Error uploading image:', uploadError);
            throw new Error('image_upload_error');
          }
        })
      );

      // Create post document with author info based on user type
      let authorName = '';
      switch (currentUser.type) {
        case 'company':
          authorName = (currentUser as Company).companyName;
          break;
        case 'contractor': {
          const contractor = currentUser as Contractor;
          authorName = `${contractor.firstName} ${contractor.lastName}`;
          break;
        }
        case 'ambassador': {
          const ambassador = currentUser as Ambassador;
          authorName = ambassador.username || `${ambassador.firstName} ${ambassador.lastName}`;
          break;
        }
      }

      const postData = {
        authorId: authUser.uid,
        userId: authUser.uid,
        authorName,
        authorPhotoURL: currentUser.profilePhotoUrl || '',
        title: formData.title,
        description: formData.description,
        images: imageUrls,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        likes: 0,
        likedBy: [],
        comments: []
      };

      try {
        if (isEditMode && editPostId) {
          // Update existing post
          const postRef = doc(db, 'posts', editPostId);
          const currentPost = await getDoc(postRef);
          if (!currentPost.exists()) {
            throw new Error('Post not found');
          }
          const currentData = currentPost.data();
          await updateDoc(postRef, {
            ...currentData,
            title: formData.title,
            description: formData.description,
            images: [...existingImageUrls, ...imageUrls],
            updatedAt: Timestamp.now()
          });
          presentToast({
            message: t('edit_post.success'),
            duration: 2000,
            color: 'success'
          });
        } else {
          // Create new post
          await addDoc(collection(db, 'posts'), postData);
          presentToast({
            message: t('new_post.success'),
            duration: 2000,
            color: 'success'
          });
        }
        history.push('/home');
      } catch (dbError) {
        console.error('Error creating post document:', dbError);
        // Try to clean up uploaded images if post creation fails
        await Promise.all(imageUrls.map(async (url) => {
          try {
            const imageRef = ref(storage, url);
            await deleteObject(imageRef);
          } catch (deleteError) {
            console.error('Error cleaning up image:', deleteError);
          }
        }));
        throw new Error('post_creation_error');
      }
    } catch (error) {
      setIsSubmitting(false);
      if (error.message === 'image_upload_error') {
        presentToast({
          message: t('new_post.image_upload_error'),
          duration: 2000,
          color: 'danger'
        });
      } else {
        presentToast({
          message: t('new_post.error'),
          duration: 2000,
          color: 'danger'
        });
      }
    }
  };

  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0: // Title step
        return !formData.title.trim();
      case 1: // Description step
        return !formData.description.trim();
      default:
        return false;
    }
  };

  // Render the current step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <TitleStep
            title={formData.title}
            maxTitleChars={maxTitleChars}
            onChange={handleTitleChange}
          />
        );
      case 1:
        return (
          <DescriptionStep
            description={formData.description}
            maxDescriptionChars={maxDescriptionChars}
            onChange={handleDescriptionChange}
          />
        );
      case 2:
        return (
          <ImagesStep
            existingImageUrls={existingImageUrls}
            newImages={formData.images}
            maxImages={maxImages}
            onAddImage={handleImageSelect}
            onRemoveExistingImage={removeExistingImage}
            onRemoveNewImage={removeNewImage}
          />
        );
      case 3:
        return (
          <ReviewStep
            title={formData.title}
            description={formData.description}
            existingImageUrls={existingImageUrls}
            newImages={formData.images}
            isEditMode={isEditMode}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <IonPage>
      {/* Header */}
      <PostHeader
        isEditMode={isEditMode}
        activeStep={activeStep}
        totalSteps={steps.length}
      />
      
      {/* Spacer to prevent content from being hidden under the fixed header */}
      <Box sx={{ height: '70px' }} />

      <IonContent>
        <div className="new-post-container">
          <Paper className="new-post-paper">
            <Stepper 
              activeStep={activeStep} 
              orientation="vertical"
              className="new-post-stepper"
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel 
                    StepIconComponent={(props) => (
                      <StepIcon 
                        {...props} 
                        icon={step.icon} 
                      />
                    )}
                  >
                    {step.label}
                  </StepLabel>
                  {activeStep === index && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      {renderStepContent()}
                      
                      {/* Navigation buttons */}
                      <StepNavigation
                        activeStep={activeStep}
                        totalSteps={steps.length}
                        onNext={handleNext}
                        onBack={handleBack}
                        isNextDisabled={isNextDisabled()}
                      />
                    </Box>
                  )}
                </Step>
              ))}
            </Stepper>
          </Paper>
        </div>
      </IonContent>
    </IonPage>
  );
};
